import { Component, OnInit } from "@angular/core";
import { ControlService } from "../../../../services/control.service";
import { CustomersService } from "../../../../services/customers.service";
import { MediasService } from "../../../../services/medias.service";
import { ReportDailiesService } from "../../../../services/report-dailies.service";

interface MediaOption {
	id: number;
	name: string;
}

interface ServiceOption {
	key: "RP" | "CP";
	label: string;
}

interface ReportRowDTO {
	mediaId: number;
	serviceKey: "RP" | "CP";
	testigos: any[];
}

interface ReportDTO {
	client_id: number | null;
	nombreReporte: string;
	fechaInicio: string;
	fechaFin: string;
	filas: ReportRowDTO[];
}

@Component({
	selector: "app-report-kpi",
	templateUrl: "./report-kpi.component.html",
	styleUrls: ["./report-kpi.component.scss"],
})
export class ReportKpiComponent implements OnInit {
	isAdmin = false;

	clientes: any[] = [];
	private clientesMap = new Map<number, string>();
	report: ReportDTO;

	/* Campos generales (ngModel) */
	client_id: number | null = null;
	nombreReporte = "";
	fechaInicio = "";
	fechaFin = "";
	previsualizar: boolean = false;

	resumenPorMedia: any[] = [];
	metaKpi: any = null;

	/* Control de validación visual */
	submitted = false;

	/* Catálogo de medias (ejemplo). */
	medias: any[] = [];
	mediasfiltered: any[] = [];
	previewSource: any[] = [];

	/* Catálogo de servicios con key y label. */
	serviceTypes: ServiceOption[] = [
		{
			key: "RP",
			label: "Reproducciones publicitarias en pantallas del sistema NetRide",
		},
	];

	/* Filas dinámicas (ngModel) */
	filas: Array<{
		mediaId: number | null;
		serviceKey: "RP" | "CP" | null;
		testigos: any[];
	}> = [];

	/* Datos y configuración inicial de la gráfica para evitar undefined */
	barChartClienteLabels: string[] = [];
	barChartClienteData: Array<{ data: number[]; label: string }> = [];
	barChartType: string = "bar";
	barChartOptionsByMedia: any = {
		responsive: true,
		maintainAspectRatio: false,
		scales: {
			xAxes: [
				{
					ticks: {
						autoSkip: false,
					},
				},
			],
			yAxes: [
				{
					ticks: {
						min: 0,
						beginAtZero: true,
					},
				},
			],
		},
		legend: {
			display: true,
		},
	};
	barChartColours: any[] = [];
	barChartLegend: boolean = true;
	previewGroupsWithCharts: Array<any> = [];

	horizontalBarOptionsByMedia: any = {
		responsive: true,
		maintainAspectRatio: false,
		scales: {
			xAxes: [
				{
					ticks: {
						min: 0,
						beginAtZero: true,
					},
				},
			],
			yAxes: [
				{
					ticks: {
						autoSkip: false,
					},
				},
			],
		},
		legend: {
			display: true,
		},
	};

	comparisonBarOptionsByMedia: any = {
		responsive: true,
		maintainAspectRatio: false,
		scales: {
			yAxes: [
				{
					ticks: {
						min: 0,
						beginAtZero: true,
					},
				},
			],
			xAxes: [
				{
					ticks: {
						autoSkip: false,
					},
				},
			],
		},
		legend: {
			display: false,
		},
		tooltips: {
			callbacks: {
				label: function (tooltipItem: any, data: any) {
					const label = data.labels?.[tooltipItem.index] || "";
					const value =
						data.datasets?.[tooltipItem.datasetIndex]?.data?.[
							tooltipItem.index
						] || 0;

					return `${label}: ${value}`;
				},
			},
		},
	};

	/* Opciones para gráfica de dona por media (Chart.js v2) */
	doughnutOptionsByMedia: any = {
		responsive: true,
		maintainAspectRatio: false,
		cutoutPercentage: 50,
		legend: {
			display: true,
			position: "left",
		},
		tooltips: {
			callbacks: {
				label: function (tooltipItem: any, data: any) {
					const idx = tooltipItem.index;
					const label = data.labels[idx] || "";
					const value = data.datasets[0].data[idx] || 0;

					const total = (data.datasets[0].data || []).reduce(
						(a: number, b: number) => a + Number(b || 0),
						0,
					);
					const pct =
						total > 0
							? ((Number(value) / total) * 100).toFixed(2)
							: "0.00";

					return `${label}: ${value} (${pct}%)`;
				},
			},
		},
	};

	constructor(
		private mediasService: MediasService,
		private controlService: ControlService,
		private customersService: CustomersService,
		private reporteKpi: ReportDailiesService,
	) {}

	async ngOnInit(): Promise<void> {
		this.controlService.isAdmin().subscribe((res) => {
			this.isAdmin = res.is_admin;
		});

		if (!this.isAdmin) {
			this.mediasfiltered =
				await this.mediasService.getMediasByType("VIDEO");
			const user = JSON.parse(localStorage.getItem("user"));
			this.client_id = user.client_id;
		}

		this.addFila();

		this.customersService.datatable(1, {}).subscribe((res: any) => {
			if (res?.data) {
				this.clientes = res.data;
				res.data.forEach((cliente) => {
					this.clientesMap.set(
						cliente.id,
						cliente.nombre_comercial ?? cliente.name,
					);
				});
			}
		});
	}

	/* Crea una fila nueva */
	addFila(): void {
		this.filas.push({
			mediaId: null,
			serviceKey: null,
			testigos: [],
		});
	}

	/* Elimina fila */
	removeFila(index: number): void {
		if (index < 0 || index >= this.filas.length) return;
		this.filas.splice(index, 1);
	}

	/* Por ahora: testigos vacío */
	seleccionarTestigos(index: number): void {
		if (index < 0 || index >= this.filas.length) return;
		this.filas[index].testigos = [];
	}

	/* Valida campos requeridos (sin Reactive Forms) */
	isValid(): boolean {
		const nombreOk = (this.nombreReporte || "").trim().length > 0;
		const inicioOk = !!this.fechaInicio;
		const finOk = !!this.fechaFin;

		const clienteOk = this.isAdmin ? this.client_id !== null : true;

		const filasOk =
			this.filas.length > 0 &&
			this.filas.every((f) => !!f.mediaId && !!f.serviceKey);

		return nombreOk && inicioOk && finOk && clienteOk && filasOk;
	}

	async getMediasByClient(event: any): Promise<void> {
		var result = await this.mediasService.getMediasByClient(event.value);
		this.mediasfiltered = result;
	}

	/* Construye el JSON del reporte */
	async generarReporte(): Promise<ReportDTO | null> {
		this.submitted = true;

		if (!this.isValid()) return null;

		const report: ReportDTO = {
			client_id: this.client_id,
			nombreReporte: (this.nombreReporte || "").trim(),
			fechaInicio: this.fechaInicio,
			fechaFin: this.fechaFin,
			filas: this.filas.map((f) => ({
				mediaId: Number(f.mediaId),
				serviceKey: f.serviceKey as "RP" | "CP",
				testigos: [],
			})),
		};

		const result = await this.reporteKpi.getKPI(report);

		this.report = report;
		/* Guarda el dataset que alimenta las tablas */
		this.previewSource = Array.isArray(result.tabla_por_unidad)
			? result.tabla_por_unidad
			: (result?.tabla_por_unidad ?? []);

		/* Resumen por media (para encabezados por tabla) */
		this.resumenPorMedia = Array.isArray(result?.resumen_por_media)
			? result.resumen_por_media
			: [];

		/* Meta general (opcional por si la usa arriba) */
		this.metaKpi = result?.meta ?? null;

		// console.log(result)
		// console.log(this.previewSource);
		// console.log(this.resumenPorMedia);

		this.prepareGroupsWithCharts();

		/* Activa modo previsualización */
		this.previsualizar = true;
		return report;
	}

	prepareGroupsWithCharts(): void {
		const groups = this.getPreviewGroups();

		this.previewGroupsWithCharts = groups.map((g) => {
			const hbar = this.buildHorizontalBarByMedia(g);
			const donut = this.buildDoughnutByMedia(g);
			const comparisonBar = this.buildComparisonBarByMedia(g);
			const comparisonBarDuration =
				this.buildComparisonBarByMediaDuration(g);

			return {
				...g,
				hbarLabels: hbar.labels,
				hbarDatasets: hbar.datasets,

				donutLabels: donut.labels,
				donutDatasets: donut.datasets,

				comparisonBarLabels: comparisonBar.labels,
				comparisonBarDatasets: comparisonBar.datasets,

				comparisonBarDurationLabels: comparisonBarDuration.labels,
				comparisonBarDurationdatasets: comparisonBarDuration.datasets,
			};
		});
		console.log(this.previewGroupsWithCharts);
	}

	editParameters(): void {
		this.previsualizar = false;
	}

	async downloadReport(): Promise<void> {
		try {
			console.log(this.report)
			const response = await this.reporteKpi.downloadKPI(this.report);
			const blob = response.body;

			let fileName = "reporte.docx";
			const contentDisposition = response.headers.get(
				"content-disposition",
			);

			if (contentDisposition) {
				const match = contentDisposition.match(/filename="?([^"]+)"?/);
				if (match?.[1]) {
					fileName = match[1];
				}
			}

			const fileURL = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = fileURL;
			link.download = fileName;
			link.click();

			window.URL.revokeObjectURL(fileURL);
		} catch (error) {
			console.error("Error al descargar el reporte Word:", error);
		}
	}

	/* Obtiene el nombre del cliente */
	getClienteNombre(): string {
		if (!this.isAdmin) return "Cliente asignado";
		if (!this.client_id) return "Sin seleccionar";
		return this.clientesMap.get(this.client_id) ?? `ID ${this.client_id}`;
	}

	/* Agrupa por media_id y calcula totales */
	getPreviewGroups(): Array<{
		media_id: number;
		media_nombre: string;
		rows: any[];
		total_reproducciones: number;
		total_segundos: number;
		categorias: Array<{ category_id: number; category_nombre: string }>;
		resumen: any | null;
	}> {
		const rows = this.previewSource || [];

		/* Índice rápido: resumenPorMedia por media_id */
		const resumenMap = new Map<number, any>();
		(this.resumenPorMedia || []).forEach((r) => {
			const id = Number(r.media_id);
			if (Number.isFinite(id)) resumenMap.set(id, r);
		});

		const map = new Map<number, any[]>();
		rows.forEach((r) => {
			const id = Number(r.media_id);
			if (!Number.isFinite(id)) return;

			if (!map.has(id)) map.set(id, []);
			map.get(id)!.push(r);
		});

		const groups = Array.from(map.entries()).map(([media_id, list]) => {
			const media_nombre =
				list[0]?.media_nombre ??
				resumenMap.get(media_id)?.media_nombre ??
				`Media ${media_id}`;

			const total_reproducciones = list.reduce(
				(acc, x) => acc + Number(x.total_reproducciones || 0),
				0,
			);

			const total_segundos = list.reduce(
				(acc, x) => acc + Number(x.segundos_reproducidos || 0),
				0,
			);

			const categoriasMap = new Map<
				number,
				{ category_id: number; category_nombre: string }
			>();
			list.forEach((x) => {
				(x.categorias || []).forEach((c: any) => {
					const cid = Number(c.category_id);
					if (!Number.isFinite(cid)) return;

					if (!categoriasMap.has(cid)) {
						categoriasMap.set(cid, {
							category_id: cid,
							category_nombre: c.category_nombre,
						});
					}
				});
			});

			return {
				media_id,
				media_nombre,
				rows: list.sort((a, b) =>
					(a.unidad || "").localeCompare(b.unidad || ""),
				),
				total_reproducciones,
				total_segundos,
				categorias: Array.from(categoriasMap.values()),
				resumen: resumenMap.get(media_id) ?? null,
			};
		});

		return groups.sort((a, b) =>
			(a.media_nombre || "").localeCompare(b.media_nombre || ""),
		);
	}

	buildHorizontalBarByMedia(g: { rows: any[] }): {
		labels: string[];
		datasets: any[];
	} {
		const rows = Array.isArray(g.rows) ? [...g.rows] : [];

		/* Orden descendente por reproducciones */
		rows.sort(
			(a, b) =>
				Number(b.total_reproducciones || 0) -
				Number(a.total_reproducciones || 0),
		);

		const labels: string[] = [];
		const data: number[] = [];

		rows.forEach((r: any) => {
			labels.push(String(r.unidad ?? "SIN UNIDAD"));
			data.push(Number(r.total_reproducciones || 0));
		});

		/* Colores suficientes para todas las unidades */
		const backgroundColor = this.buildPieColors(labels.length, 0.65);
		const hoverBackgroundColor = this.buildPieColors(labels.length, 0.85);

		return {
			labels,
			datasets: [
				{
					data,
					label: "Reproducciones por unidad",
					backgroundColor,
					hoverBackgroundColor,
					borderWidth: 1,
				},
			],
		};
	}

	/* Construye una gráfica de dona por media con reproducciones por unidad */
	buildDoughnutByMedia(g: { rows: any[] }): {
		labels: string[];
		datasets: any[];
	} {
		const rows = Array.isArray(g.rows) ? [...g.rows] : [];

		/* Orden descendente por reproducciones para mantener consistencia visual */
		rows.sort(
			(a, b) =>
				Number(b.total_reproducciones || 0) -
				Number(a.total_reproducciones || 0),
		);

		const labels: string[] = [];
		const data: number[] = [];

		rows.forEach((r: any) => {
			labels.push(String(r.unidad ?? "SIN UNIDAD"));
			data.push(Number(r.total_reproducciones || 0));
		});

		const backgroundColor = this.buildPieColors(labels.length, 0.65);
		const hoverBackgroundColor = this.buildPieColors(labels.length, 0.85);

		return {
			labels,
			datasets: [
				{
					data,
					backgroundColor,
					hoverBackgroundColor,
					borderWidth: 1,
				},
			],
		};
	}

	/* Genera un arreglo de colores HSL para N barras, con transparencia configurable. */
	buildPieColors(count: number, alpha: number): string[] {
		const colors: string[] = [];

		const baseHue = 210; // Azul (210° en HSL)

		for (let i = 0; i < count; i++) {
			const lightness = 35 + i * (40 / Math.max(1, count));
			// Va de oscuro a claro (35% → 75%)

			colors.push(`hsla(${baseHue}, 70%, ${lightness}%, ${alpha})`);
		}

		return colors;
	}

	buildComparisonBarByMedia(g: { resumen: any | null }): {
		labels: string[];
		datasets: any[];
	} {
		const resumen = g?.resumen ?? {};

		const unidadesSpotTotales = Number(resumen.total_reproducciones || 0);
		const reproduccionesContratadas = Number(
			resumen.reproducciones_contratadas || 0,
		);

		return {
			labels: ["Reproducciones totales", "Reproducciones contratadas"],
			datasets: [
				{
					data: [unidadesSpotTotales, reproduccionesContratadas],
					backgroundColor: [
						"rgba(54, 162, 235, 0.65)",
						"rgba(255, 159, 64, 0.65)",
					],
					hoverBackgroundColor: [
						"rgba(54, 162, 235, 0.85)",
						"rgba(255, 159, 64, 0.85)",
					],
					borderColor: [
						"rgba(54, 162, 235, 1)",
						"rgba(255, 159, 64, 1)",
					],
					borderWidth: 1,
				},
			],
		};
	}

	buildComparisonBarByMediaDuration(g: { resumen: any | null }): {
		labels: string[];
		datasets: any[];
	} {
		const resumen = g?.resumen ?? {};

		const DuracionTotal = Number(resumen.duration_seconds || 0);
		const DuracionSpotBase = 20;

		return {
			labels: ["Duración total", "Duración Base de Spot"],
			datasets: [
				{
					data: [DuracionTotal, DuracionSpotBase],
					backgroundColor: [
						"rgba(54, 162, 235, 0.65)",
						"rgba(255, 159, 64, 0.65)",
					],
					hoverBackgroundColor: [
						"rgba(54, 162, 235, 0.85)",
						"rgba(255, 159, 64, 0.85)",
					],
					borderColor: [
						"rgba(54, 162, 235, 1)",
						"rgba(255, 159, 64, 1)",
					],
					borderWidth: 1,
				},
			],
		};
	}

	/* 
  Almacena el estado abierto/cerrado de la tabla por media.
  La llave es el media_id y el valor indica si está visible.
*/
	mediaTablesState: Record<number, boolean> = {};

	/* 
  Alterna la visibilidad de la tabla de detalle de una media.
*/
	toggleMediaTable(mediaId: number): void {
		this.mediaTablesState[mediaId] = !this.mediaTablesState[mediaId];
	}

	/* 
  Indica si la tabla de detalle de una media está abierta.
*/
	isMediaTableOpen(mediaId: number): boolean {
		return !!this.mediaTablesState[mediaId];
	}
}
